/* global Word, console */
import type { ReviewTileItem, SearchResult } from "@/types/tile.types";
import {
  normalizeTextWith,
  findWholeWordMatches,
  findSubstringMatches,
  collectTileMatches,
} from "./tile-search-match.utils";
import { calculateRelevanceScore } from "./tile-search-score.utils";

export interface UseTileSearchOptions {
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
  onSearchComplete?: (results: SearchResult[]) => void;
  highlightInDocument?: boolean;
  caseSensitive?: boolean;
  wholeWords?: boolean;
}

export function buildFindMatchesFn(
  caseSensitive: boolean,
  wholeWords: boolean
): (text: string, searchTerm: string) => { startIndex: number; endIndex: number }[] {
  const normalize = (t: string) => normalizeTextWith(t, caseSensitive);
  return (text, searchTerm) => {
    const nt = normalize(text);
    const ns = normalize(searchTerm);
    return wholeWords ? findWholeWordMatches(nt, ns, caseSensitive) : findSubstringMatches(nt, ns);
  };
}

export async function tryDelimiterSearch(
  truncatedText: string,
  searchInDocument: (term: string) => Promise<boolean>
): Promise<boolean> {
  const delimiters = [":", ";"];
  for (const delimiter of delimiters) {
    if (truncatedText.includes(delimiter)) {
      const parts = truncatedText.split(delimiter);
      const firstPart = parts[0]?.trim();
      if (firstPart != null) {
        const result = await searchInDocument(firstPart);
        if (result) return true;
      }
    }
  }
  return false;
}

export async function executeSearchTiles(
  tiles: ReviewTileItem[],
  searchTerm: string,
  findMatches: (text: string, term: string) => { startIndex: number; endIndex: number }[],
  normalizeText: (text: string) => string
): Promise<SearchResult[]> {
  const results: SearchResult[] = [];
  for (const tile of tiles) {
    const { tileMatches, totalMatches } = collectTileMatches(tile, searchTerm, findMatches);
    if (tileMatches.length > 0) {
      const score = calculateRelevanceScore(
        tileMatches,
        totalMatches,
        tile,
        searchTerm,
        normalizeText
      );
      results.push({ tile, matches: tileMatches, score });
    }
  }
  results.sort((a, b) => b.score - a.score);
  return results;
}

export async function executeSearchInDocument(
  searchTerm: string,
  caseSensitive: boolean,
  wholeWords: boolean,
  options?: UseTileSearchOptions
): Promise<boolean> {
  if (searchTerm.trim() === "") {
    options?.onError?.("Search term is required");
    return false;
  }

  try {
    return await Word.run(async (context) => {
      const wordResults = context.document.body.search(searchTerm, {
        ignorePunct: true,
        ignoreSpace: true,
        matchCase: caseSensitive,
        matchWholeWord: wholeWords,
      });
      context.load(wordResults, "items");
      await context.sync();
      if (wordResults.items.length > 0) {
        wordResults.items[0]?.select();
        await context.sync();
        options?.onSuccess?.(`Found ${wordResults.items.length} matches in document`);
        return true;
      }
      return false;
    });
  } catch (error: unknown) {
    console.error("Error searching in document:", error);
    const msg = error instanceof Error ? error.message : "Unknown error";
    options?.onError?.(`Document search failed: ${msg}`);
    return false;
  }
}

export async function executeClearHighlights(options?: UseTileSearchOptions): Promise<boolean> {
  try {
    await Word.run(async (context) => {
      const ht = context.document.body.search("", { ignorePunct: true, ignoreSpace: true });
      context.load(ht, "items");
      await context.sync();
      ht.items.forEach((item) => {
        item.font.highlightColor = "";
        item.font.bold = false;
      });
      await context.sync();
      options?.onSuccess?.("Highlights cleared from document");
    });
    return true;
  } catch (error: unknown) {
    console.error("Error clearing highlights:", error);
    const msg = error instanceof Error ? error.message : "Unknown error";
    options?.onError?.(`Failed to clear highlights: ${msg}`);
    return false;
  }
}
